import FilterListIcon from '@material-ui/icons/FilterList'
import CategoryIcon from '@material-ui/icons/Category'
import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import {
    AdminFilters,
    AdminFilterValues,
    AdminFiltersSlotRenderArgs
} from '../../Molecules/AdminFilters/AdminFilters'
import { SelectFilter, SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    CodeAddModify,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage,
    CodeListLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getCodeList } from '../../../../React/Common/API/ItemsGetters'
import {
    CodeList as CodeListRows,
    CodeListItem,
    CodeListResponse,
    CodeListRow
} from '../../../../NonReact/Types/CodeListTypes'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import AddCodeModal from '../../../../React/CodesModal/Modal/AddCodeModal'
import { CodeTypesStringValues, CodeTypesValues } from '../../../../React/CodesModal/Constants/Constants'
import { deleteCodesMany } from '../../../../NonReact/common/API/EntityAPIClient'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import {
    CODE_TYPE_ACCOUNT,
    CODE_TYPE_ACD_NOT_AVAILABLE,
    DEFAULT_ROWS_PER_PAGE_OPTIONS,
    FIRST_PAGE_INDEX,
    SCOPE_ALL,
    SCOPE_DETACHED,
    SCOPE_SERVICEWIDE,
    SCOPE_SYSTEMWIDE
} from './Constants'

/**
 * True when the filter value is a concrete TelSys or partition selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony or partition option value from admin filters.
 * @returns Whether the value should be sent to the code list API as a restricting parameter.
 */
const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean =>
    isVarNotEmpty(value) && value !== ALL_VALUE && value !== ANY_VALUE_STRING_NUMERIC

/**
 * Returns the description when non-empty, otherwise falls back to the numeric/string ID (or an "any" label when the ID is an [All] sentinel).
 *
 * @param description - Human-readable description from the API row.
 * @param id - Numeric or string identifier to use as fallback.
 * @param anyLabel - Optional label to render when {@link id} matches the All/Any sentinel value.
 * @returns Display string for the cell.
 */
const getDescriptionOrIdValue = (description: string, id: string | number, anyLabel?: string): string => {
    if (isStringNotEmpty(description)) return description
    if (isVarNotEmpty(anyLabel) && (id === ALL_VALUE || id === ANY_VALUE_STRING_NUMERIC)) return anyLabel
    return String(id)
}

/**
 * Maps list row {@link CodeListItem.CodeType} to the admin API path segment used by get-by-name (CODEINFO).
 * The list returns {@link CodeTypesStringValues} from the server; numeric 0/10 and filter values are also accepted.
 *
 * @param codeType - Raw code type from the list row or API item.
 * @returns {@link CodeTypesStringValues.ACCOUNT_CODE} or {@link CodeTypesStringValues.AGENT_NA}.
 */
const rowCodeTypeToApiPathSegment = (codeType: string | number | undefined): string => {
    const t: string = String(codeType ?? EMPTY_STRING).trim()

    if (t === CodeTypesStringValues.AGENT_NA || t === CODE_TYPE_ACD_NOT_AVAILABLE) return CodeTypesStringValues.AGENT_NA
    if (t === CodeTypesStringValues.ACCOUNT_CODE || t === CODE_TYPE_ACCOUNT) return CodeTypesStringValues.ACCOUNT_CODE

    const n: number = Number.parseInt(t, 10)

    if (n === CodeTypesValues.AGENT_NA) return CodeTypesStringValues.AGENT_NA
    return CodeTypesStringValues.ACCOUNT_CODE
}

/**
 * Routed Codes list view.
 *
 * @returns Codes list page content.
 */
export const CodeList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundles([
        RouterLang.NAME,
        CodeListLanguage.NAME,
        SidebarMenuLanguage.NAME,
        GenericAdminListLanguage.NAME,
        GenericAdminFilterLanguage.NAME,
        CodeAddModify.NAME
    ])

    const [codeList, setCodeList]: ReactState<Nullable<CodeListRows>> = useState<Nullable<CodeListRows>>(null)
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isCodeModalOpen, setCodeModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedCodeRow, setSelectedCodeRow]: ReactState<Nullable<CodeListRow>> =
        useState<Nullable<CodeListRow>>(null)
    const [codeModalViewOnly, setCodeModalViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [scopeFilter, setScopeFilter]: ReactState<Nullable<SelectFilterOption>> =
        useState<Nullable<SelectFilterOption>>(null)
    const [codeTypeFilter, setCodeTypeFilter]: ReactState<Nullable<SelectFilterOption>> =
        useState<Nullable<SelectFilterOption>>(null)
    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const scopeValue: string = String(scopeFilter?.value ?? SCOPE_ALL)
    const codeTypeValue: string = String(codeTypeFilter?.value ?? EMPTY_STRING)
    /** Tel/partition restrict the CME code list whenever a concrete selection is made (any scope), same pattern as CRMIntegrationList. */
    const listTelSysId: Definable<string | number> = isConcreteTelSysOrPartitionFilterValue(
        filters?.telephonyServer?.value
    )
        ? (filters?.telephonyServer?.value)
        : undefined
    const listPartIDInTelSys: Definable<string | number> =
        isVarNotEmpty(listTelSysId) && isConcreteTelSysOrPartitionFilterValue(filters?.partition?.value)
            ? (filters?.partition?.value)
            : undefined
    const listCodeTypeFilter: Definable<string> = codeTypeValue === EMPTY_STRING ? undefined : codeTypeValue
    const listCodeScopeFilter: Definable<string> = scopeValue === SCOPE_ALL ? undefined : scopeValue
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_CODES, filters, userInfo?.Scope?.CMEnvironmentID)

    const getCodes = useCallback((): void => {
        if (IsVarEmpty(listCmeId)) {
            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        if (listNoTelephonyServer) {
            if (isMounted.current) {
                setCodeList([])
                setTotalCount(0)
                setLoading((currentLoading: number): number => currentLoading - 1)
            }
            return
        }

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        getCodeList(
            listCmeId,
            listIncludeRecursive,
            listTelSysId,
            listPartIDInTelSys,
            listCodeTypeFilter,
            currentPage * rowsPerPage,
            rowsPerPage,
            sort,
            searchQuery,
            listCodeScopeFilter
        )
            .then((response: Nullable<CodeListResponse>): void => {
                if (isMounted.current) {
                    const rows: CodeListRows =
                        response?.Data?.Items?.map(
                            (item: CodeListItem, index: number): CodeListRow => ({
                                key: item.CodeGUID ?? `code-row-${index}`,
                                Description: item.Description ?? EMPTY_STRING,
                                Code: item.Code ?? EMPTY_STRING,
                                CodeType: String(item.CodeType ?? EMPTY_STRING),
                                TelSysID: item.TelSysID ?? EMPTY_STRING,
                                TelSysDescription: item.TelSysDescription ?? EMPTY_STRING,
                                PartIDInTelSys: item.PartIDInTelSys ?? EMPTY_STRING,
                                PartitionDescription: item.PartitionDescription ?? EMPTY_STRING,
                                PartitionGUID: item.PartitionGUID,
                                CodeGUID: item.CodeGUID ?? EMPTY_STRING
                            })
                        ) ?? []

                    setCodeList(rows)
                    setTotalCount(Number(response?.Data?.Total) || 0)
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        listCmeId,
        listIncludeRecursive,
        listNoTelephonyServer,
        listSearch,
        listTelSysId,
        listPartIDInTelSys,
        listCodeTypeFilter,
        listCodeScopeFilter,
        pagination,
        sort,
        userInfo?.Scope?.CMEnvironmentID,
        scopeFilter,
        codeTypeFilter
    ])

    const scopeOptions = useMemo(
        (): { value: string; label: string }[] => [
            { value: SCOPE_ALL, label: language.get(CodeListLanguage.CodeList_Scope_All) },
            { value: SCOPE_SERVICEWIDE, label: language.get(CodeListLanguage.CodeList_Scope_ServiceWide) },
            { value: SCOPE_SYSTEMWIDE, label: language.get(CodeListLanguage.CodeList_Scope_SystemWide) },
            { value: SCOPE_DETACHED, label: language.get(CodeListLanguage.CodeList_Scope_Detached) }
        ],
        [language]
    )

    const codeTypeOptions = useMemo(
        (): { value: string; label: string }[] => [
            { value: EMPTY_STRING, label: language.get(CodeListLanguage.CodeList_Type_All) },
            { value: CODE_TYPE_ACCOUNT, label: language.get(CodeListLanguage.CodeList_Type_Account) },
            {
                value: CODE_TYPE_ACD_NOT_AVAILABLE,
                label: language.get(CodeListLanguage.CodeList_Type_ACD_NotAvailable)
            }
        ],
        [language]
    )

    useEffect((): void => {
        if (IsVarEmpty(scopeFilter) && scopeOptions.length > 0) {
            setScopeFilter(scopeOptions[0] ?? null)
        }

        if (IsVarEmpty(codeTypeFilter) && codeTypeOptions.length > 0) {
            setCodeTypeFilter(codeTypeOptions[0] ?? null)
        }
    }, [scopeFilter, codeTypeFilter, scopeOptions, codeTypeOptions])

    const columns: DataTableColumn<CodeListRow, keyof CodeListRow>[] = useMemo(
        (): DataTableColumn<CodeListRow, keyof CodeListRow>[] => [
            {
                headerName: language.get(CodeListLanguage.CodeList_Column_Description),
                field: 'Description',
                key: 'code-list-description',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(CodeListLanguage.CodeList_Column_Code),
                field: 'Code',
                key: 'code-list-code',
                sortable: true
            },
            {
                headerName: language.get(CodeListLanguage.CodeList_Column_Type),
                field: 'CodeType',
                key: 'code-list-type',
                sortable: true
            },
            {
                headerName: language.get(CodeListLanguage.CodeList_Column_TelSys),
                field: 'TelSysID',
                key: 'code-list-telsys',
                renderCell: (row: CodeListRow): string => getDescriptionOrIdValue(row.TelSysDescription, row.TelSysID)
            },
            {
                headerName: language.get(CodeListLanguage.CodeList_Column_Partition),
                field: 'PartIDInTelSys',
                key: 'code-list-partition',
                renderCell: (row: CodeListRow): string =>
                    getDescriptionOrIdValue(
                        row.PartitionDescription,
                        row.PartIDInTelSys,
                        language.get(CodeListLanguage.CodeList_Partition_Any)
                    )
            }
        ],
        [language]
    )

    const codeModalFilterCMEID: string = listCmeId ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID
    const codeModalFilterTelSysID: Definable<number> = isConcreteTelSysOrPartitionFilterValue(
        filters?.telephonyServer?.value
    )
        ? Number(filters?.telephonyServer?.value)
        : undefined

    let selectedRowModalFilterCodeTypeLabel: Definable<string>

    if (!IsVarEmpty(selectedCodeRow)) {
        const isAgentNa: boolean =
            rowCodeTypeToApiPathSegment(selectedCodeRow.CodeType) === CodeTypesStringValues.AGENT_NA

        selectedRowModalFilterCodeTypeLabel = isAgentNa
            ? language.get(CodeAddModify.CodeAddModify_ACDNotAvailable)
            : language.get(CodeAddModify.CodeAddModify_AccountAuthorisation)
    }

    const openCodeModal = (row: Nullable<CodeListRow>, viewOnly: boolean): void => {
        setSelectedCodeRow(row)
        setCodeModalViewOnly(viewOnly)
        setCodeModalOpen(true)
    }

    useEffect(getCodes, [getCodes])

    const codeModalModeSuffix: string = codeModalViewOnly ? 'v' : 'e'
    const codeModalKey: string = selectedCodeRow
        ? `${selectedCodeRow.Code}-${selectedCodeRow.TelSysID}-${selectedCodeRow.PartitionGUID ?? EMPTY_STRING}-${codeModalModeSuffix}`
        : `add-${codeModalFilterTelSysID ?? 'all'}`

    return (
        <div className='code-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_code} />
            <div className='code-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    onChange={(values: AdminFilterValues): void => setFilters(values)}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    additionalFilterSkeletonCount={2} // NOSONAR
                    additionalFilters={({ isLoading }: AdminFiltersSlotRenderArgs): ReactElement => (
                        <>
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        setScopeFilter(value)
                                        setPagination(([, rowsPerPage]: PaginationInfo): PaginationInfo => [
                                            FIRST_PAGE_INDEX,
                                            rowsPerPage
                                        ])
                                    }}
                                    options={scopeOptions}
                                    label={language.get(CodeListLanguage.CodeList_Scope_Label)}
                                    icon={<FilterListIcon />}
                                    disabled={isLoading}
                                    value={scopeFilter}
                                    defaultValue={SCOPE_ALL}
                                />
                            </div>
                            <div>
                                <SelectFilter
                                    onChange={(value: Nullable<SelectFilterOption>): void => {
                                        setCodeTypeFilter(value)
                                        setPagination(([, rowsPerPage]: PaginationInfo): PaginationInfo => [
                                            FIRST_PAGE_INDEX,
                                            rowsPerPage
                                        ])
                                    }}
                                    options={codeTypeOptions}
                                    label={language.get(CodeListLanguage.CodeList_Type_Label)}
                                    icon={<CategoryIcon />}
                                    disabled={isLoading}
                                    value={codeTypeFilter}
                                    defaultValue={EMPTY_STRING}
                                />
                            </div>
                        </>
                    )}
                    onRefresh={getCodes}
                />
                <DataTable<CodeListRow, keyof CodeListRow>
                    permissionName={AdminBackendPermissionNames.CODE}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={codeList ?? []}
                    defaultRowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    onToolbarAddClick={(): void => openCodeModal(null, false)}
                    onToolbarChangeClick={([selectedItem]: CodeListRow[]): void =>
                        openCodeModal(selectedItem ?? null, false)
                    }
                    onToolbarViewClick={([selectedItem]: CodeListRow[]): void =>
                        openCodeModal(selectedItem ?? null, true)
                    }
                    onToolbarDeleteClick={(selectedItems: CodeListRow[]): void => {
                        setLoading((currentLoading: number): number => currentLoading + 1)
                        deleteCodesMany(
                            selectedItems.map(
                                (
                                    row: CodeListRow
                                ): {
                                    TelSysID: string | number
                                    PartIDInTelSys: string | number
                                    Code: string
                                    CodeType: string | number
                                } => ({
                                    TelSysID: row.TelSysID,
                                    PartIDInTelSys: row.PartIDInTelSys,
                                    Code: row.Code,
                                    CodeType: rowCodeTypeToApiPathSegment(row.CodeType)
                                })
                            )
                        )
                            .then(getCodes)
                            .catch(displayError)
                            .finally((): void => {
                                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isCodeModalOpen ? (
                    <AddCodeModal
                        key={codeModalKey}
                        refreshAction={getCodes}
                        filterCMEID={codeModalFilterCMEID}
                        filterTelSysID={
                            isVarNotEmpty(selectedCodeRow) ? Number(selectedCodeRow.TelSysID) : codeModalFilterTelSysID
                        }
                        filterPartitionID={selectedCodeRow?.PartitionGUID}
                        filterPartIDInTelSys={selectedCodeRow?.PartIDInTelSys ?? listPartIDInTelSys}
                        filterCodeType={selectedRowModalFilterCodeTypeLabel}
                        codeTypePathSegment={
                            isVarNotEmpty(selectedCodeRow) ? rowCodeTypeToApiPathSegment(selectedCodeRow.CodeType) : undefined
                        }
                        selectedCode={selectedCodeRow?.Code}
                        viewOnly={codeModalViewOnly}
                        onModalExited={(): void => {
                            setCodeModalOpen(false)
                            setSelectedCodeRow(null)
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
